<template>
  <a-form class="flex-grow-1 d-flex flex-column overflow-y-hidden" :form="form" @submit="onSubmit">
    <div v-if="isLoading" class="d-flex justify-content-center align-items-center pt-35-px">
      <Spinner />
    </div>
    <div class="a-body p-20-px flex-grow-1 d-flex flex-column overflow-y-auto">
      <a-alert
        v-if="loadFailed"
        type="error"
        show-icon
        message="The account options could not be loaded."
      >
        <template slot="description">
          <p v-if="loadError" class="mb-2">
            {{ loadError }}
          </p>
          <a-button type="primary" size="small" @click="loadOptions">
            Try again
          </a-button>
        </template>
      </a-alert>
      <transition name="slide-up">
        <div v-if="!isLoading && !loadFailed">
          <a-row :gutter="24">
            <a-col :span="6">
              <a-form-item label="Owner Id">
                <a-input
                  :value="user.ownerId"
                  placeholder="Owner ID"
                  disabled
                />
              </a-form-item>
            </a-col>
            <a-col :span="6">
              <a-form-item label="SMS Balance">
                <a-input
                  :value="user.smsBalance"
                  placeholder="SMS Balance"
                  disabled
                />
              </a-form-item>
            </a-col>
            <a-col :span="6">
              <a-form-item label="Create date" has-feedback>
                <a-date-picker
                  class="full-w"
                  placeholder="Choose Date"
                  :value="createDate"
                  :format="DATE_FORMAT.DDMMMYYYYHHmm"
                  disabled
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="6">
              <a-form-item label="Timezone">
                <a-select v-decorator="['timezone']" size="large" :disabled="isSaving">
                  <a-select-option v-for="timezone in timezones" :key="timezone.name" :value="timezone.name">
                    {{ timezone.name }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :span="6">
              <a-form-item label="Status">
                <a-select v-decorator="['status']" size="large" :disabled="isSaving">
                  <a-select-option v-for="(status, index) in statuses" :key="index" :value="status">
                    <span class="text-capitalize">
                      {{ status }}
                    </span>
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :span="6">
              <a-form-item label="Expiry date" has-feedback>
                <a-date-picker
                  v-decorator="[
                    'expiryDate',
                    {
                      rules: [
                        { required: true, message: 'Please select expiry date.' }
                      ]
                    }
                  ]"
                  class="full-w"
                  placeholder="Choose Date"
                  :disabled="isSaving"
                  :disabled-date="disabledDate"
                  :disabled-time="disabledRangeTime"
                  :show-time="{ format: 'HH:mm' }"
                  :format="DATE_FORMAT.DDMMMYYYYHHmm"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col v-for="plan in PLANS" :key="plan.field" :span="6">
              <a-form-item :label="plan.label">
                <a-input
                  v-decorator="[
                    plan.field,
                    {
                      rules: [
                        { required: true, message: `The ${plan.field} limit field is required.` },
                        { validator: validateIntegerNumber }
                      ]
                    }
                  ]"
                  :placeholder="plan.label"
                  :disabled="isSaving"
                />
              </a-form-item>
            </a-col>
          </a-row>
        </div>
      </transition>
    </div>
    <div class="a-footer">
      <a-button size="large" class="mr-3" :disabled="!isDirty || isSaving" @click="undo">
        Undo
      </a-button>
      <a-button
        size="large"
        type="primary"
        html-type="submit"
        :loading="isSaving"
        :disabled="!isDirty || isSaving"
      >
        Save
      </a-button>
    </div>
  </a-form>
</template>

<script>
  import moment from 'moment';
  import { range, toString } from 'lodash';
  import { DATE_FORMAT } from '@constants/dateFormat';
  import DataService from '@shared/services/data.service';
  import UserManagementService from '../../services/UserManagement.service';
  import dirtyFormMixin from '../../mixins/dirtyForm.mixin';
  import { EXPIRY_DATE_FORMAT, PLANS } from '../../consts/UserManagement.const';

  export default {
    name: 'AccountTab',
    mixins: [dirtyFormMixin],
    props: {
      user: { type: Object, required: true }
    },
    data() {
      return {
        DATE_FORMAT,
        PLANS,
        statuses: [],
        timezones: [],
        isLoading: true,
        loadFailed: false,
        loadError: ''
      };
    },
    computed: {
      createDate() {
        return moment(this.user.createDate);
      }
    },
    watch: {
      // Show what the server kept after a save. Skipped until the form is first filled
      // (the first fill reads the latest user), and while the person is editing again.
      user() {
        if (this.pristine !== null && !this.isDirty) {
          this.setFormValues(this.valuesFromUser());
          this.markPristine();
        }
      }
    },
    beforeCreate() {
      this.form = this.$form.createForm(this, {
        name: 'user-account-form',
        onValuesChange: () => this.touchForm()
      });
    },
    created() {
      this.loadOptions();
    },
    methods: {
      async loadOptions() {
        this.isLoading = true;
        this.loadFailed = false;
        this.loadError = '';
        try {
          const [enumData, timezoneData] = await Promise.all([DataService.getEnums(), DataService.getTimezones()]);
          this.statuses = (enumData && enumData.user && enumData.user.status) || [];
          this.timezones = timezoneData || [];
        } catch (error) {
          this.loadFailed = true;
          this.loadError = (error.messages || []).join(' ');
        } finally {
          this.isLoading = false;
        }
        if (!this.loadFailed) {
          // The fields are registered when the form renders, which is after isLoading flips.
          await this.$nextTick();
          this.setFormValues(this.valuesFromUser());
          this.markPristine();
        }
      },
      valuesFromUser() {
        const { timezone, status, expiryDate, gold, silver, bronze, trial } = this.user;
        return {
          timezone,
          status,
          expiryDate: expiryDate ? moment(expiryDate).format(EXPIRY_DATE_FORMAT) : '',
          gold: toString(gold),
          silver: toString(silver),
          bronze: toString(bronze),
          trial: toString(trial)
        };
      },
      getFormValues() {
        const { expiryDate, ...values } = this.form.getFieldsValue();
        return { ...values, expiryDate: expiryDate ? expiryDate.format(EXPIRY_DATE_FORMAT) : '' };
      },
      setFormValues({ expiryDate, ...values }) {
        this.form.setFieldsValue({ ...values, expiryDate: expiryDate ? moment(expiryDate) : null });
      },
      onSubmit(e) {
        e.preventDefault();
        this.form.validateFieldsAndScroll((err, values) => {
          if (!err) {
            this.save(values);
          }
        });
      },
      async save({ gold, silver, bronze, trial, timezone, expiryDate, status }) {
        const body = {
          gold_limit: +gold,
          silver_limit: +silver,
          bronze_limit: +bronze,
          trial_limit: +trial,
          timezone,
          expiry_date: expiryDate.format(EXPIRY_DATE_FORMAT),
          status
        };
        this.isSaving = true;
        try {
          await UserManagementService.updateUserById(this.user.customerId, body);
          this.$showSuccessMessage('Updated user');
          this.markPristine();
          this.$emit('saved');
        } catch (error) {
          this.$showMultiErrorMessage(error.messages);
        } finally {
          this.isSaving = false;
        }
      },
      validateIntegerNumber(rule, value, callback) {
        if (value && (isNaN(value) || +value < 0)) {
          callback('Please input positive integer number');
        } else {
          callback();
        }
      },
      disabledDate(current) {
        return current && current.isBefore(moment().startOf('day'));
      },
      disabledRangeTime() {
        const nowTime = new Date();
        const expDateAsMomentValue = this.form.getFieldValue('expiryDate');
        return {
          disabledHours: () => (!expDateAsMomentValue || expDateAsMomentValue.isSame(moment(), 'day') ? range(0, nowTime.getHours()) : []),
          disabledMinutes: () => (!expDateAsMomentValue || expDateAsMomentValue.isSame(moment(), 'hour') ? range(0, nowTime.getMinutes()) : [])
        };
      }
    }
  };
</script>
